/**
 * 3D Rotation — corpus pré-rendu (paradigme Shepard-Metzler 1971).
 *
 * MVP — SVG iso projections ; full WebGL/three.js Sprint 4+ (LiDAR ARKit + tactile rotation).
 *
 * On compose 30 paires (référence, candidate) à partir de 6 shapes templates
 * Tetris-like (3-4 squares connectés), chacune projetée en isométrique 2D
 * dans un viewbox 80x80. Pour chaque shape, on précompute des variantes
 * rotation (0°, 45°, 90°, 135°, 180°) et leur miroir horizontal.
 *
 * Le 2-AFC : "identique" (rotation pure) vs "miroir" (reflet + rotation).
 *
 * Distribution :
 *   - 6 items @ 15° (5 identiques, hum 50/50 par tier visé)
 *   - 6 items @ 45°
 *   - 6 items @ 90°
 *   - 6 items @ 135°
 *   - 6 items @ 180°
 *   - 50/50 identique vs miroir global (15 / 15).
 */
/** Tetris L-piece (4 squares). */
const SHAPE_L = [
    [-15, -25], [5, -25], [5, 5], [25, 5], [25, 25], [-15, 25],
];
/** Tetris T-piece (4 squares). */
const SHAPE_T = [
    [-25, -10], [25, -10], [25, 10], [10, 10], [10, 30], [-10, 30], [-10, 10], [-25, 10],
];
/** Tetris S-piece (4 squares). */
const SHAPE_S = [
    [-25, 5], [-5, 5], [-5, -15], [25, -15], [25, 5], [5, 5], [5, 25], [-25, 25],
];
/** Tetris Z-piece (4 squares, miroir de S). */
const SHAPE_Z = [
    [25, 5], [5, 5], [5, -15], [-25, -15], [-25, 5], [-5, 5], [-5, 25], [25, 25],
];
/** Triangle "step" 3-square. */
const SHAPE_STEP = [
    [-25, 25], [-25, 5], [-5, 5], [-5, -15], [15, -15], [15, 25],
];
/** Plus-sign 5-square (croix). */
const SHAPE_CROSS = [
    [-10, -25], [10, -25], [10, -10], [25, -10], [25, 10], [10, 10], [10, 25],
    [-10, 25], [-10, 10], [-25, 10], [-25, -10], [-10, -10],
];
const SHAPES = {
    L: SHAPE_L,
    T: SHAPE_T,
    S: SHAPE_S,
    Z: SHAPE_Z,
    STEP: SHAPE_STEP,
    CROSS: SHAPE_CROSS,
};
// =====================================================================
// Helpers — rotation 2D + miroir + projection iso légère + sérialisation.
// =====================================================================
/** Rotation 2D autour de l'origine. */
function rotate(pt, deg) {
    const rad = (deg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return [pt[0] * cos - pt[1] * sin, pt[0] * sin + pt[1] * cos];
}
/** Miroir horizontal (axe Y). */
function mirrorX(pt) {
    return [-pt[0], pt[1]];
}
/**
 * Projection iso légère : on simule une vue 3D en inclinant la shape de
 * 25° sur l'axe X (squash vertical) — donne l'illusion qu'on regarde la
 * forme de "haut-côté" comme un assemblage de cubes Shepard-Metzler.
 */
function isoProject(pt) {
    const skewY = 0.5; // léger squash vertical (cos(60°))
    const skewX = 0.25; // décalage horizontal selon Y
    return [pt[0] + pt[1] * skewX, pt[1] * skewY];
}
/** Convertit liste de points en string SVG centré sur viewbox 80x80. */
function toSvgPoints(pts) {
    const cx = 40;
    const cy = 40;
    return pts
        .map((p) => {
        const proj = isoProject(p);
        return `${(proj[0] + cx).toFixed(2)},${(proj[1] + cy).toFixed(2)}`;
    })
        .join(' ');
}
/**
 * Compose la candidate : applique rotation θ, optionnellement miroir AVANT
 * la rotation (= "miroir" du paradigme = reflet d'abord, puis rotation).
 */
function buildCandidate(shape, angleDeg, mirror) {
    const transformed = shape.map((p) => {
        const m = mirror ? mirrorX(p) : p;
        return rotate(m, angleDeg);
    });
    return transformed;
}
/**
 * Plan déterministe : 30 items, 6 par tier d'angle, 50/50 identique vs miroir.
 * Pour chaque tier on cycle les 6 shapes pour assurer diversité visuelle.
 */
const SHAPE_ORDER = ['L', 'T', 'S', 'Z', 'STEP', 'CROSS'];
const ANGLE_TIERS = [15, 45, 90, 135, 180];
function buildSpecs() {
    const specs = [];
    let mirrorToggle = false;
    for (const angleDeg of ANGLE_TIERS) {
        for (let i = 0; i < SHAPE_ORDER.length; i += 1) {
            const shape = SHAPE_ORDER[i];
            // Alterne identique/miroir, garantit 15/15 sur 30 items.
            const isIdentical = !mirrorToggle;
            specs.push({ shape, angleDeg, isIdentical });
            mirrorToggle = !mirrorToggle;
        }
    }
    return specs;
}
const SPECS = buildSpecs();
export const ROTATION_CORPUS = SPECS.map((spec, idx) => {
    const base = SHAPES[spec.shape];
    const refPoints = toSvgPoints(base);
    const candidate = buildCandidate(base, spec.angleDeg, !spec.isIdentical);
    const candPoints = toSvgPoints(candidate);
    const seq = String(idx + 1).padStart(3, '0');
    return {
        id: `mrt-${seq}-${spec.shape.toLowerCase()}-${spec.angleDeg}${spec.isIdentical ? 'i' : 'm'}`,
        refPoints,
        candPoints,
        angleDeg: spec.angleDeg,
        isIdentical: spec.isIdentical,
    };
});
/** Filtre items par tier d'angle (degrés). */
export function itemsByAngle(angleDeg) {
    return ROTATION_CORPUS.filter((i) => i.angleDeg === angleDeg);
}
/** Tiers d'angle disponibles (degrés). */
export const ANGLE_BUCKETS = ANGLE_TIERS;
//# sourceMappingURL=corpus.js.map